/**
 * Tolkning av tidsstamplar fran API:et.
 *
 * Backend lagrar och returnerar naiv UTC-tid: "2026-09-14T11:41:13" utan
 * tidszonsmarkor. JavaScript tolkar en sadan strang som LOKAL tid, sa en
 * kommentar skriven for en minut sedan visades som "2 h sedan" i svensk
 * sommartid. Felet vaxer med tidszonsavstandet och ar osynligt for den som
 * utvecklar i UTC.
 *
 * Hjalparen antar UTC nar markoren saknas, och lamnar tidsstamplar som redan
 * bar zon ("...Z" eller "+02:00") orarda.
 */

/** ISO-strang → Date, dar saknad tidszon tolkas som UTC. */
export function tolkaTidpunkt(iso) {
  if (!iso) return null;
  if (iso instanceof Date) return iso;
  const s = String(iso).trim();
  if (!s) return null;
  // Bar strangen redan zon? "Z", "+HH:MM" eller "-HH:MM" pa slutet.
  const harZon = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(s);
  const normaliserad = harZon ? s : `${s.replace(' ', 'T')}Z`;
  const d = new Date(normaliserad);
  return Number.isFinite(d.getTime()) ? d : null;
}

/** Millisekunder sedan tidpunkten, eller null om den inte gar att tolka. */
export function millisekunderSedan(iso) {
  const d = tolkaTidpunkt(iso);
  return d ? Date.now() - d.getTime() : null;
}

/** "nyss" / "12 min sedan" / "3 h sedan" / "2 d sedan", annars null. */
export function relativTid(iso) {
  const diff = millisekunderSedan(iso);
  if (diff === null) return null;
  // Klockor gar isar nagon sekund mellan server och klient. Utan golvet
  // visas en helt farsk post som "om 1 minut".
  const min = Math.floor(Math.max(diff, 0) / 60000);
  if (min < 1) return 'nyss';
  if (min < 60) return `${min} min sedan`;
  const timmar = Math.floor(min / 60);
  if (timmar < 24) return `${timmar} h sedan`;
  const dagar = Math.floor(timmar / 24);
  if (dagar < 7) return `${dagar} d sedan`;
  return null; // anroparen formaterar som datum
}

/* KanPlan anvands i Sverige. Absoluta klockslag ska darfor visas i svensk tid
   oavsett vilken tidszon enheten star i — en tidrapport last pa resa ska visa
   samma klockslag som den visade pa kontoret. */
export const TIDSZON = 'Europe/Stockholm';

/** "8 sep 13:41" i svensk tid, eller tom strang om tiden inte gar att tolka. */
export function datumOchKlockslag(iso) {
  const d = tolkaTidpunkt(iso);
  if (!d) return '';
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: TIDZON_ELLER_UTC(),
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d).replace(',', '');
}

/** "2026-09-08 13:41" i svensk tid — for historik och loggar dar aret spelar roll. */
export function fulltDatumOchKlockslag(iso) {
  const d = tolkaTidpunkt(iso);
  if (!d) return '';
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: TIDZON_ELLER_UTC(),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

/** "13:41" i svensk tid. */
export function klockslag(iso) {
  const d = tolkaTidpunkt(iso);
  if (!d) return '';
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: TIDZON_ELLER_UTC(),
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

/**
 * Kalenderdatum som "2026-10-16" i webblasarens egen tid.
 *
 * For datum som anvandaren valt i en kalender (lokal midnatt). Anvand
 * ALDRIG toISOString().slice(0, 10) for sadana: det raknar om till UTC, och
 * lokal midnatt i svensk tid ar foregaende dag i UTC. En revision flyttad
 * till 16/10 sparades sa som 15/10 (fynd 2026-10-17).
 */
export function lokaltDatum(datum) {
  if (!(datum instanceof Date) || Number.isNaN(datum.getTime())) return '';
  const tva = (n) => String(n).padStart(2, '0');
  return `${datum.getFullYear()}-${tva(datum.getMonth() + 1)}-${tva(datum.getDate())}`;
}

/** Dagens datum i svensk tid som "2026-10-16", rätt även mellan 00 och 02. */
export function dagensDatum(nu = new Date()) {
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: TIDZON_ELLER_UTC(), year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(nu);
}

/** "2026-10-16T13:41" i webbläsarens tid, för <input type="datetime-local">. */
export function lokalDatumTid(datum = new Date()) {
  if (!(datum instanceof Date) || Number.isNaN(datum.getTime())) return '';
  const tva = (n) => String(n).padStart(2, '0');
  return `${lokaltDatum(datum)}T${tva(datum.getHours())}:${tva(datum.getMinutes())}`;
}

/** Värde från ett datetime-local-fält (lokal tid) som naiv UTC, så som backend lagrar. */
export function lokalTillNaivUtc(varde) {
  const d = varde ? new Date(varde) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toISOString().slice(0, 19) : null;
}

/* Aldre webblasare utan full IANA-databas kastar pa okand tidszon. Da ar UTC
   ett battre fall tillbaka an ett kastat undantag mitt i en render. */
function TIDZON_ELLER_UTC() {
  try {
    new Intl.DateTimeFormat('sv-SE', { timeZone: TIDSZON }).format(new Date());
    return TIDSZON;
  } catch {
    return 'UTC';
  }
}
